import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Home, TrendingDown, Clock, Star, MessageCircle } from "lucide-react";
import { track } from "@/lib/analytics";

interface CaseStudy {
  title: string;
  location: string;
  propertyType: string;
  size: string;
  yearInstalled: number;
  energySavings: string;
  automations: string;
  paybackTime: string;
  highlights: string[];
  clientQuote: string;
  clientName: string;
  image?: string;
}

const caseStudies: CaseStudy[] = [
  {
    title: "Moderne Villa Overbetuwe",
    location: "Elst, Gelderland", 
    propertyType: "Nieuwbouw villa",
    size: "280m²",
    yearInstalled: 2023,
    energySavings: "€1.200/jaar (42%)",
    automations: "1.200/maand",
    paybackTime: "6,5 jaar",
    highlights: [
      "Multiroom audio in 8 zones",
      "Intelligente zonwering + verlichting",
      "EV charging op zonne-energie",
      "Warmtepomp optimalisatie"
    ],
    clientQuote: "Het systeem overtreft alle verwachtingen. We hebben niet meer het gevoel dat we in een 'slim huis' wonen - het voelt gewoon perfect aan.",
    clientName: "Familie Van der Berg"
  },
  {
    title: "Gerenoveerde Boerderij",
    location: "Herveld, Overbetuwe",
    propertyType: "Monumentale boerderij",
    size: "450m²", 
    yearInstalled: 2022,
    energySavings: "€1.800/jaar (38%)",
    automations: "2.100/maand",
    paybackTime: "7,2 jaar",
    highlights: [
      "Heritage-friendly installatie",
      "Complexe klimaatzonering",
      "Beveiligingsintegratie", 
      "Energiemanagement 22 zones"
    ],
    clientQuote: "SOL-VI heeft perfect gebalanceerd tussen moderne technologie en respect voor ons monumentale pand. Fantastisch vakmanschap.",
    clientName: "M. Janssen - Restaurateur"
  },
  {
    title: "Duurzame Stadswoning",
    location: "Arnhem Centrum",
    propertyType: "Rijtjeswoning", 
    size: "140m²",
    yearInstalled: 2023,
    energySavings: "€850/jaar (35%)",
    automations: "950/maand",
    paybackTime: "5,8 jaar",
    highlights: [
      "Minimale bekabeling (Tree)",
      "Philips Hue integratie",
      "Smart heating scheduling",
      "Airco + vloerverwarming sync"
    ],
    clientQuote: "Van app-chaos naar één systeem dat alles regelt. De energiebesparing was een mooie bonus, maar het comfort is onbetaalbaar.",
    clientName: "Dr. Peters - Huisarts"
  }
];

export const SmartHomeCaseStudies = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-muted/20 to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              Klantenervaringen Gelderland
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Echte Resultaten van <span className="text-primary">Loxone Projecten</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Ontdek hoe SOL-VI eigenaren in Gelderland heeft geholpen met intelligente automatisering 
              en aanzienlijke energiebesparingen.
            </p>
          </div>

          <div className="grid gap-8 mb-12">
            {caseStudies.map((study, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <CardTitle className="text-xl mb-2">{study.title}</CardTitle>
                      <CardDescription className="flex items-center gap-4 text-base">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {study.location}
                        </div>
                        <div className="flex items-center gap-1">
                          <Home className="h-4 w-4" />
                          {study.propertyType} • {study.size}
                        </div>
                      </CardDescription>
                    </div>
                    <Badge variant="secondary">
                      Geïnstalleerd {study.yearInstalled}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-3 gap-6 mb-6">
                    <div className="text-center p-4 bg-primary/5 rounded-lg">
                      <TrendingDown className="h-6 w-6 text-primary mx-auto mb-2" />
                      <div className="font-semibold text-primary">{study.energySavings}</div>
                      <div className="text-sm text-muted-foreground">Energiebesparing</div>
                    </div>
                    <div className="text-center p-4 bg-primary/5 rounded-lg">
                      <Clock className="h-6 w-6 text-primary mx-auto mb-2" />
                      <div className="font-semibold text-primary">{study.automations}</div>
                      <div className="text-sm text-muted-foreground">Automatiseringen</div>
                    </div>
                    <div className="text-center p-4 bg-primary/5 rounded-lg">
                      <Star className="h-6 w-6 text-primary mx-auto mb-2" />
                      <div className="font-semibold text-primary">{study.paybackTime}</div>
                      <div className="text-sm text-muted-foreground">Terugverdientijd</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Belangrijkste realisaties:</h4>
                    <div className="grid md:grid-cols-2 gap-2">
                      {study.highlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
                          {highlight}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-muted/30 p-4 rounded-lg border-l-4 border-primary">
                    <blockquote className="text-muted-foreground italic mb-2">
                      "{study.clientQuote}"
                    </blockquote>
                    <cite className="text-sm font-medium text-primary">
                      — {study.clientName}
                    </cite>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Summary Stats */}
          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">Gecombineerde Resultaten</h3>
                <p className="text-muted-foreground">
                  Deze drie projecten samen tonen de kracht van Loxone in Gelderland
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary">38%</div>
                  <div className="text-sm text-muted-foreground">Gem. besparing</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary">4.250</div>
                  <div className="text-sm text-muted-foreground">Automatiseringen/maand</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary">€3.850</div>
                  <div className="text-sm text-muted-foreground">Totale besparing/jaar</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary">6,5</div>
                  <div className="text-sm text-muted-foreground">Gem. terugverdientijd</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="text-center mt-12">
            <p className="text-lg font-medium mb-6">
              Bent u benieuwd naar de mogelijkheden voor uw woning?
            </p>
            <Button 
              size="lg"
              onClick={() => {
                track("smarthome_casestudies_cta");
                (window as any).Calendly?.initPopupWidget?.({
                  url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800',
                  pageSettings: {
                    backgroundColor: 'ffffff',
                    hideEventTypeDetails: false,
                    hideLandingPageDetails: false,
                    primaryColor: 'ff7800',
                    textColor: '4d5055'
                  }
                });
              }}
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              Plan Gratis Haalbaarheidsanalyse
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SmartHomeCaseStudies;